import { makeRequest } from "./client";
import type { User } from "./types";

export const usersApi = {
  getAll: async (schoolId: string, token: string): Promise<User[]> => {
    const response = await makeRequest<{ users: User[] }>(
      `/v1/org/${schoolId}/users`,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    return response?.users || [];
  },

  delete: async (userId: string, token: string): Promise<void> => {
    throw new Error("User deletion not implemented yet");
  },

  removeFromSchool: async (
    userId: string,
    schoolId: string,
    token: string
  ): Promise<void> => {
    const response = await makeRequest<{ message: string }>(
      `/v1/org/${schoolId}/users/${userId}`,
      {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      }
    );

    if (!response) {
      throw new Error("Failed to remove user from organization");
    }
  },

  getInvitations: async (
    schoolId: string,
    token: string
  ): Promise<
    Array<{
      invitationId: string;
      email?: string;
      phoneNumber?: string;
      role: string;
      accessLegacyMessages: boolean;
      legacyAccessDays?: number;
      memberAlias?: string;
      status: "pending" | "accepted" | "expired";
      invitedBy: string;
      invitedAt: string;
      expiresAt: string;
    }>
  > => {
    const response = await makeRequest<{
      invitations: Array<{
        invitationId: string;
        email?: string;
        phoneNumber?: string;
        role: string;
        accessLegacyMessages: boolean;
        legacyAccessDays?: number;
        memberAlias?: string;
        status: "pending" | "accepted" | "expired";
        invitedBy: string;
        invitedAt: string;
        expiresAt: string;
      }>;
      count: number;
    }>(`/v1/org/${schoolId}/invitations`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!response) {
      throw new Error("Failed to fetch invitations");
    }

    return response.invitations || [];
  },

  getJoinRequests: async (
    schoolId: string,
    token: string
  ): Promise<
    Array<{
      requestId: string;
      email: string;
      learnerName: string;
      learnerSurname: string;
      parentName: string;
      cellNumber: string;
      timestamp: string;
      status: string;
      gradeClass?: string;
      teacherName?: string;
      relationship?: string;
    }>
  > => {
    const response = await makeRequest<{
      joinRequests: Array<{
        requestId: string;
        email: string;
        learnerName: string;
        learnerSurname: string;
        parentName: string;
        cellNumber: string;
        timestamp: string;
        status: string;
        gradeClass?: string;
        teacherName?: string;
        relationship?: string;
      }>;
      count: number;
    }>(`/v1/org/${schoolId}/join-requests`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!response) {
      throw new Error("Failed to fetch join requests");
    }

    return response.joinRequests || [];
  },

  acceptJoinRequest: async (
    schoolId: string,
    requestId: string,
    token: string
  ): Promise<void> => {
    const response = await makeRequest<{ message: string }>(
      `/v1/org/${schoolId}/join-requests/${requestId}`,
      {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status: "accepted" }),
      }
    );

    if (!response) {
      throw new Error("Failed to update join request");
    }
  },

  dismissJoinRequest: async (
    schoolId: string,
    requestId: string,
    token: string
  ): Promise<void> => {
    const response = await makeRequest<{ message: string }>(
      `/v1/org/${schoolId}/join-requests/${requestId}`,
      {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status: "dismissed" }),
      }
    );

    if (!response) {
      throw new Error("Failed to dismiss join request");
    }
  },

  deleteInvitation: async (
    invitationId: string,
    schoolId: string,
    token: string
  ): Promise<void> => {
    const response = await makeRequest<{ message: string }>(
      `/v1/org/${schoolId}/invitations/${invitationId}`,
      {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      }
    );

    if (!response) {
      throw new Error("Failed to delete invitation");
    }
  },

  updateInvitationAlias: async (
    invitationId: string,
    schoolId: string,
    alias: string,
    token: string
  ): Promise<{ alias: string | null }> => {
    const response = await makeRequest<{
      message: string;
      invitationId: string;
      alias: string | null;
      memberAlias: string | null;
    }>(`/v1/org/${schoolId}/invitations/${invitationId}`, {
      method: "PATCH",
      headers: { Authorization: `Bearer ${token}` },
      body: JSON.stringify({ alias }),
    });

    if (!response) {
      throw new Error("Failed to update invitation alias");
    }

    return { alias: response.alias ?? response.memberAlias ?? null };
  },

  getUserCircles: async (
    userId: string,
    schoolId: string,
    token: string
  ): Promise<{
    circles: Array<{
      circleId: string;
      name: string;
      description: string;
      role: string;
      joinedAt: string;
      isPublic: boolean;
      alias?: string;
    }>;
  }> => {
    const response = await makeRequest<{
      circles: Array<{
        circleId: string;
        name: string;
        description: string;
        role: string;
        joinedAt: string;
        isPublic: boolean;
        alias?: string;
      }>;
    }>(`/v1/org/${schoolId}/users/${userId}/circles`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!response) {
      throw new Error("Failed to get user circles");
    }

    return {
      ...response,
      circles: response.circles ?? [],
    };
  },

  checkApprovalStatus: async (
    schoolId: string,
    token: string
  ): Promise<{ isApproved: boolean; featureFlags?: Record<string, boolean> }> => {
    const response = await makeRequest<{
      message: string;
      featureFlags?: Record<string, boolean>;
      subcircles: Array<{
        subcircleId: string;
        name: string;
        isApproved: boolean;
        subCircle: {
          id: string;
          parentOrgId: string;
        };
      }>;
    }>(`/v1/org/${schoolId}/circles`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    const mainCircle = response.subcircles?.find(
      (circle) =>
        circle.subcircleId === schoolId &&
        circle.subCircle?.parentOrgId === schoolId
    );

    return {
      isApproved: mainCircle?.isApproved ?? false,
      featureFlags: response.featureFlags,
    };
  },
};
